import { Link } from 'react-router';
import { Routes } from '../../app/routes';
import { flatten, useHistory } from '../../data/queries';
import type { ItemEvent } from '../../domain/models';
import { useLocale, useT } from '../../l10n/i18n';
import { eventLabel, eventStyle, toneColors } from '../core/presentation';
import { EmployeeAvatar } from '../core/widgets/EmployeeAvatar';
import { EmptyState, ErrorView, Skeleton } from '../core/widgets/common';
import { byDay, dayLabel, eventTarget, eventWords, timeOfDay } from './days';
import { LoadMore } from './LoadMore';
import './history.css';

// Everything that happened, newest first, grouped by day (the mobile app's history screen).

export function HistoryPage() {
  const t = useT();
  const locale = useLocale();
  const q = useHistory();
  const events = flatten(q.data);

  let body;
  if (!events && q.isError) {
    body = <ErrorView error={q.error} onRetry={() => void q.refetch()} />;
  } else if (!events) {
    body = (
      <div className="hist-skeleton">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i}>
            <Skeleton width={36} height={36} radius={12} />
            <Skeleton />
          </div>
        ))}
      </div>
    );
  } else if (events.length === 0) {
    body = <EmptyState title={t('historyEmpty')} />;
  } else {
    const now = new Date();
    body = (
      <>
        {byDay(events).map(({ day, events: list }) => {
          const heading = `hist-day-${day.getTime()}`;
          return (
            <section key={day.getTime()} className="hist-day" aria-labelledby={heading}>
              <h2 id={heading} className="overline hist-day-title">
                {dayLabel(t, locale, day, now)}
              </h2>
              <ul className="hist-list">
                {list.map((e) => (
                  <EventRow key={e.id} event={e} />
                ))}
              </ul>
            </section>
          );
        })}
        <LoadMore hasMore={q.hasNextPage} loading={q.isFetchingNextPage} onMore={() => void q.fetchNextPage()} />
      </>
    );
  }

  return (
    <div className="page">
      <header className="page-header">
        <h1 className="display-small">{t('navHistory')}</h1>
      </header>
      {body}
    </div>
  );
}

function EventRow({ event: e }: { event: ItemEvent }) {
  const t = useT();
  const locale = useLocale();
  const style = eventStyle(e);
  const Icon = style.icon;
  const emp = e.employee;
  const words = eventWords(e);
  return (
    <li className="hist-row">
      <span className="hist-face">
        {emp && <EmployeeAvatar avatar={emp.avatar} color={emp.color} size={36} label={emp.name} />}
        <span className="hist-badge" style={{ color: toneColors(style.tone).fg }}>
          <Icon size={12} strokeWidth={2.6} aria-hidden />
        </span>
      </span>
      <div className="hist-text">
        {/* The whole line opens the item; the title says which. */}
        <Link to={Routes.item(eventTarget(e))} className="hist-title title-small">
          {e.itemTitle}
        </Link>
        <p className="body-small">{eventLabel(t, locale, e)}</p>
        {words && <p className="body-medium muted hist-words">“{words}”</p>}
      </div>
      <time className="body-small faint hist-time" dateTime={e.createdAt.toISOString()} title={e.createdAt.toLocaleString(locale)}>
        {timeOfDay(locale, e.createdAt)}
      </time>
    </li>
  );
}
